import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface Step {
    name: string;
    /** Displayed text; defaults to `name`. */
    label?: string | null;
    is_closed: boolean;
    /** How a closed step ends the path, declared by the server: `won` is drawn green, anything else red. */
    outcome?: "won" | "lost" | null;
}

export interface PathLayoutProps {
    ordered_steps: Step[];
    current_step: Step | null;
    /** @deprecated Legacy registration page only, which computes its steps client-side: use `outcome`. */
    won_step_names?: string[];
}

const stepLabel = (step: Step) => step.label || step.name;

export function PathLayout(props: PathLayoutProps) {
    if (!props.current_step || props.ordered_steps.length === 0) return null;
    return <PathSteps {...props} current_step={props.current_step} />;
}

function PathSteps(props: PathLayoutProps & { current_step: Step }) {
    const isWon = props.current_step.outcome === "won" || (props.won_step_names ?? []).includes(props.current_step.name);

    const getStepColor = (stepIndex: number) => {
        const currentIndex = props.ordered_steps.findIndex(s => s.name === props.current_step.name);

        if (isWon) return "bg-shamrock-green";

        if (props.current_step.is_closed || stepIndex > currentIndex) return "bg-gray-100";
        if (stepIndex < currentIndex) return "bg-shamrock-green";
        return "bg-azure-blue";
    };

    const getStartElementColor = () => {
        const firstStep = props.ordered_steps[0];

        if (isWon) return "bg-shamrock-green";
        if (props.current_step.name === firstStep.name) return "bg-azure-blue";
        if (props.current_step.is_closed) return "bg-gray-100";
        return "bg-shamrock-green";
    };

    const scroller = useRef<HTMLDivElement>(null);
    const current = useRef<HTMLDivElement>(null);

    // Every label is shown whole, so the path may overflow: it scrolls, and opens centered on the current step.
    useLayoutEffect(() => {
        const box = scroller.current;
        const step = current.current;
        if (!box || !step) return;
        box.scrollLeft = Math.max(0, step.offsetLeft + step.offsetWidth / 2 - box.clientWidth / 2);
    }, [props.current_step.name]);

    return (
        <div ref={scroller} className="w-full min-w-0 overflow-x-auto" data-testid="path-scroller">
            <div className="relative flex w-max min-w-full flex-row items-center gap-1 px-4 py-2">
                <div className={cn("absolute left-0 top-0 h-9 w-7 rounded-l-full", getStartElementColor())}></div>

                {props.ordered_steps
                    .filter(step => !step.is_closed)
                    .map((step, index) => {
                        const originalIndex = props.ordered_steps.findIndex(s => s.name === step.name);
                        const color = getStepColor(originalIndex);

                        return (
                            <div
                                key={index}
                                ref={step.name === props.current_step.name ? current : undefined}
                                className={cn(
                                    "relative flex h-5 flex-auto shrink-0 items-center text-black before:absolute before:-top-2 before:left-0 before:h-[17px] before:w-full before:skew-x-[30deg] before:content-[''] after:absolute after:-bottom-2 after:left-0 after:h-[17px] after:w-full after:skew-x-[-30deg] after:content-['']",
                                    index === 0 && "ml-1",
                                    color === "bg-gray-100" && "before:bg-gray-100 after:bg-gray-100 ",
                                    color === "bg-shamrock-green" && "text-white before:bg-shamrock-green after:bg-shamrock-green ",
                                    color === "bg-azure-blue" && "text-white before:bg-azure-blue after:bg-azure-blue"
                                )}
                            >
                                <span className={cn("relative z-20 w-full whitespace-nowrap px-4 text-center text-sm", index === 0 && "pl-1")}>{stepLabel(step)}</span>
                            </div>
                        );
                    })}
                <div
                    ref={props.current_step.is_closed ? current : undefined}
                    className={cn(
                        "relative flex h-5 flex-auto shrink-0 items-center rounded-r-full before:absolute before:-top-2 before:left-0 before:h-[17px] before:w-full before:skew-x-[30deg] before:bg-gray-100 before:content-[''] after:absolute after:-bottom-2 after:left-0 after:h-[17px] after:w-full after:skew-x-[-30deg] after:bg-gray-100 ",
                        props.current_step.is_closed
                            ? isWon
                                ? "text-white before:bg-shamrock-green after:bg-shamrock-green"
                                : "text-white before:bg-pantone-red after:bg-pantone-red"
                            : "text-black before:bg-gray-100 after:bg-gray-100"
                    )}
                >
                    <span className="relative z-10 w-full whitespace-nowrap pl-4 pr-1 text-center text-sm">{props.current_step.is_closed ? stepLabel(props.current_step) : "Closed"}</span>
                </div>
                <div className={cn("absolute right-0 top-0 h-9 w-7 rounded-r-full", props.current_step.is_closed ? (isWon ? "bg-shamrock-green" : "bg-pantone-red") : "bg-gray-100")}></div>
            </div>
        </div>
    );
}
